'use client';

import { ServiceModules, type ServiceModulesContent } from '../components';

const IP_SERVICE_CONTENT: ServiceModulesContent = {
  layout: {
    nextLink: '/airtime',
    hoverTextColor: 'text-at',
    logoColor: 'text-at',
  },
  hero: {
    backgroundColor: 'bg-navy',
    backgroundImage: '/images/services/ip_waves.jpg',
    headingStatement: 'Protecting your big ideas',
    heading: 'Intellectual Property',
    headingLargeText:
      'Every business needs to protect its assets. But in the swirl of founding a startup, IP tends to take a back seat.',
    headingSmallText:
      'Over 50% of seed companies don’t apply for trademarks and fail to patent their ideas. This can impact a company’s competitive standing, market penetrations, and even fundraising. Getting ahead of these common challenges is critical to success.',
  },
  teamProof: {
    text: 'Our team has secured more than 110 patents and 60 registered trademarks, with a patent issue rate 10% higher than the USPTO average.',
  },
  whatWeDo: {
    thinTitle: 'NEW FOR 2026',
    boldTitle: 'Proactive IP development for startups',
    textParagraphs: [
      'Now seed-stage VCs can partner with All Turtles, giving founders a simpler path to understanding and developing their IP.',
      'We’ll assess your referred companies and provide a clear roadmap for which trademarks and patents are worth pursuing, and when. We manage IP development and filing processes in close cooperation with IP law firms, so founders can focus on innovation.',
    ],
    ctaText: 'See how it works',
    ctaLink: '/proactive-ip',
    rightColumnItems: [
      {
        heading: 'IP opportunity assessment',
        paragraph:
          'Identify intellectual property opportunities and define a development path with a preliminary evaluation from All Turtles’ IP experts.',
      },
      {
        heading: 'Patent development',
        paragraph:
          'It takes on average about 3 years to get a US patent. Starting early provides stronger safeguards and a more appealing story for investors.',
      },
      {
        heading: 'Trademark development',
        paragraph:
          'A company’s brand can be its most important asset. Registering trademarks is critical to defending territory and standing out in a crowded market.',
      },
      {
        heading: 'Brand name optimization',
        paragraph:
          'We can evaluate proposed brand names to optimize registrability, reduce conflict risk, and improve the odds of a successful trademark application.',
      },
      {
        paragraph:
          'All Turtles handles discovery and development across the full patent and trademark lifecycle in long-term partnerships with law firms Westborough IP Group, LLC and Trust Tree Legal, P.C.',
        italic: true,
      },
    ],
  },
  whatMakesUsUnique: {
    points: [
      {
        boldPhrase: 'We get founders',
        description:
          'We’re a product studio ourselves, so we understand the challenges and pitfalls of building an IP portfolio quickly and effectively. We know what startups need, when they need it, and how to get it.',
      },
      {
        boldPhrase: 'Unmatched experience',
        description:
          'Our team has helped startups jumpstart their IP development for over 14 years, working with standout tech companies like Sunflower Labs, Octane Al, Carrot Fertility, Airtime, Replika, Evernote, and many more.',
      },
      {
        boldPhrase: 'An exceptional track record',
        description:
          "Our patent issue rate is 10% higher than the USPTO average, thanks to our dedication to technical quality. And we've registered more than 60 trademarks, with dozens more pending.",
      },
    ],
  },
  allTurtlesInAction: {
    backgroundColor: 'bg-navy',
    backgroundImage: '/images/services/ip_sunflower_docks.png',
    mobileBackgroundImage: '/images/services/ip_sunflower_docks.png',
    title: 'Building Sunflower Labs’ patent portfolio',
    paragraphs: [
      'As a pioneer in physical site security, Sunflower Labs has developed a system that deploys fully autonomous, AI-powered drones to protect commercial, industrial, and residential properties. They’ve turned to All Turtles time and again to help patent their inventions, and we’ve delivered with a whopping 90% success rate (compared to a USPTO average of 65%).',
      'Our ample experience across the startup spectrum has helped us identify and address Sunflower’s needs from the earliest stages of their journey through recent investment rounds from major funders. All while Sunflower’s team stays focused on delivering the best product possible.',
    ],
    testimonialPhoto: '/images/services/ip_sunflower_alex.png',
    testimonialPhotoAlt: 'Alex Pachikov, CEO of Sunflower Labs',
    testimonialQuote:
      'It’s nearly impossible for a startup to know what’s truly patentable and actually worth patenting. Working with AT helped us identify which of our ideas and developments met that bar. We uncovered more than a dozen patentable inventions. Then the AT team and their patent law firm partner handled the paperwork and managed all communications with the patent office. Without their help, we wouldn’t have the patent portfolio we have today.',
    testimonialPerson: 'Alex Pachikov',
    testimonialTitle: 'CEO, Sunflower Labs',
  },
  portfolioShowcase: [
    {
      image: {
        src: '/images/services/patent-example.png',
        alt: 'United States Patent - Landing platform with multicopter self-orientation',
        width: 800,
        height: 1067,
      },
      title: 'LANDING PLATFORM WITH MULTICOPTER SELF-ORIENTATION',
      patentNumber: 'U.S. Patent 12,391,415',
      date: 'August 19, 2025',
      linkUrl: 'https://sunflower-labs.com/patents',
      linkText: 'Sunflower Labs',
    },
    {
      image: {
        src: '/images/services/patents/USPatent12133028.jpg',
        alt: 'United States Patent - Enhancing zooming capabilities of a security camera by automatically employing a multicopter',
        width: 800,
        height: 1067,
      },
      title: 'ENHANCING ZOOMING CAPABILITIES OF A SECURITY CAMERA BY AUTOMATICALLY EMPLOYING A MULTICOPTER',
      patentNumber: 'U.S. Patent 12,133,028',
      date: 'October 29, 2024',
      linkUrl: 'https://sunflower-labs.com/patents',
      linkText: 'Sunflower Labs',
    },
    {
      image: {
        src: '/images/services/patents/USPatent11915481.jpg',
        alt: 'United States Patent - Capturing and analyzing security events and activities and generating their natural language descriptions',
        width: 800,
        height: 1067,
      },
      title:
        'CAPTURING AND ANALYZING SECURITY EVENTS AND ACTIVITIES AND GENERATING THEIR NATURAL LANGUAGE DESCRIPTIONS',
      patentNumber: 'U.S. Patent 11,915,481',
      date: 'February 27, 2024',
      linkUrl: 'https://sunflower-labs.com/patents',
      linkText: 'Sunflower Labs',
    },
    {
      image: {
        src: '/images/services/patents/USPatent11776136.jpg',
        alt: 'United States Patent - Mobile security system with multiple landing locations',
        width: 800,
        height: 1067,
      },
      title: 'MOBILE SECURITY SYSTEM WITH MULTIPLE LANDING LOCATIONS',
      patentNumber: 'U.S. Patent 11,776,136',
      date: 'October 3, 2023',
      linkUrl: 'https://sunflower-labs.com/patents',
      linkText: 'Sunflower Labs',
    },
  ],
  caseStudyLink: 'mailto:ip@all-turtles.com?subject=IP%20Services%20inquiry',
  caseStudyText: 'Let’s protect your company’s IP',
};


export const ServicePage = () => {
  return <ServiceModules content={IP_SERVICE_CONTENT} />;
};
